import React from 'react';
import { motion } from 'framer-motion';
import { ChevronLeftIcon, MinusIcon, PlusIcon, SlidersHorizontalIcon } from 'lucide-react';
import { ImplementationOption, PricingLayout, promoImplementationFee, usePricing } from '../../contexts/PricingContext';
import { ControlNumberField } from './ControlNumberField';
import { AssumptionControls } from './AssumptionControls';
const AD_SPEND_STEP = 2500;
const formatUsd = (value: number) => value.toLocaleString('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0
});
const sectionLabel = 'text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500';
const IMPLEMENTATION_OPTIONS: {
  key: ImplementationOption;
  label: string;
}[] = [{
  key: 'full',
  label: 'In full'
}, {
  key: 'split',
  label: 'Split ×3'
}, {
  key: 'promo',
  label: '$3K promo'
}];
interface ToggleRowProps {
  label: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}
function ToggleRow({
  label,
  checked,
  onChange
}: ToggleRowProps) {
  return <button type="button" role="switch" aria-checked={checked} onClick={() => onChange(!checked)} className="w-full flex items-center justify-between gap-3 py-1.5 text-left text-[13px] font-medium text-slate-300 hover:text-white transition-colors duration-150 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7FE0C3] rounded" data-id="element-1265" data-mp="a0s4axbrzens">
      <span className="whitespace-nowrap" data-id="element-1266" data-mp="3n9wmyzzz4iqf">{label}</span>
      <span aria-hidden="true" className={`relative inline-flex h-[18px] w-8 shrink-0 rounded-full transition-colors duration-150 ease-out ${checked ? 'bg-[#14684d]' : 'bg-slate-700'}`} data-id="element-1267" data-mp="3i9s3d13rr7vz">
        <span className={`absolute top-[3px] left-[3px] h-3 w-3 rounded-full bg-white transition-transform duration-150 ease-out ${checked ? 'translate-x-[14px]' : ''}`} data-id="element-1268" data-mp="3raq6lh6dvbdi" />
      </span>
    </button>;
}

/**
 * Quiet left-side panel for tailoring the proposal. Every change updates the
 * shared deck settings, so the Partnership page and the PDF stay in step.
 */
interface ProposalControlsProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
}
export function ProposalControls({
  open,
  onOpenChange
}: ProposalControlsProps) {
  const {
    moneyBackGuarantee,
    setMoneyBackGuarantee,
    guaranteeRevealed,
    setGuaranteeRevealed,
    hasRecruiter,
    setHasRecruiter,
    hasLeader,
    setHasLeader,
    goToMarket,
    setGoToMarket,
    adSpend,
    setAdSpend,
    cpaLow,
    setCpaLow,
    cpaHigh,
    setCpaHigh,
    hiresLow,
    hiresHigh,
    pricingModel,
    setPricingModel,
    pricingLayout,
    setPricingLayout,
    setImplementationOption,
    isPromo,
    splitBuildFee,
    promoDeadline,
    setPromoDeadline,
    regularImplementationFee
  } = usePricing();
  const stepAdSpend = (direction: 1 | -1) => setAdSpend(Math.max(AD_SPEND_STEP, adSpend + direction * AD_SPEND_STEP));
  if (!open) {
    return <button type="button" onClick={() => onOpenChange(true)} aria-expanded={false} aria-label="Open proposal settings" className="no-print fixed left-4 top-20 z-[110] hidden lg:flex flex-col items-center gap-2 rounded-xl border border-slate-800 bg-[#0d141f]/95 px-2 py-3 text-slate-400 shadow-lg hover:text-white transition-colors duration-150 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7FE0C3]" data-id="element-1269" data-mp="32ugxonaobrt5">
        <SlidersHorizontalIcon className="w-4 h-4" aria-hidden="true" data-id="element-1270" data-mp="2cg12ai0brb1l" />
        <span className="[writing-mode:vertical-rl] rotate-180 text-[10px] font-bold uppercase tracking-[0.14em]" data-id="element-1271" data-mp="27ngepy80hu6i">
          Tailor
        </span>
      </button>;
  }
  const promoActive = isPromo;
  const splitActive = splitBuildFee;
  const promoPrice = formatUsd(promoImplementationFee(hasRecruiter));
  const implementationHint = promoActive && splitActive ? `${promoPrice} (regularly ${formatUsd(regularImplementationFee)}), split: a third at signing, then Months 2 and 3, if they sign by:` : splitActive ? 'A third at signing, then Months 2 and 3.' : promoActive ? `${promoPrice} one time (regularly ${formatUsd(regularImplementationFee)}), if they sign by:` : `${formatUsd(regularImplementationFee)} one time, at signing.`;
  return <motion.aside aria-label="Proposal settings" initial={{
    opacity: 0,
    x: -8
  }} animate={{
    opacity: 1,
    x: 0
  }} transition={{
    duration: 0.18,
    ease: [0.23, 1, 0.32, 1]
  }} onWheel={e => e.stopPropagation()} className="no-print fixed left-4 top-20 z-[110] hidden lg:block w-60 max-h-[calc(100vh-6rem)] overflow-y-auto overscroll-contain rounded-xl border border-slate-800 bg-[#0d141f]/95 px-4 py-4 shadow-lg" data-id="element-1272" data-mp="3jzlhz5mgpgu8">
      <div className="flex items-center justify-between" data-id="element-1273" data-mp="313n4nuy21n79">
        <div className={sectionLabel} data-id="element-1274" data-mp="uc4fwbw26x3o">Tailor proposal</div>
        <button type="button" onClick={() => onOpenChange(false)} aria-label="Close proposal settings" className="-mr-1.5 grid place-items-center h-6 w-6 rounded text-slate-500 hover:text-white transition-colors duration-150 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7FE0C3]" data-id="element-1275" data-mp="za5v2lqd6c9v">
          <ChevronLeftIcon className="w-4 h-4" data-id="element-1276" data-mp="1i7zdz9vmgbdh" />
        </button>
      </div>
      <div className="mt-2" data-id="element-1277" data-mp="1dfg2qjkepsbu">
        <ToggleRow label="Corporate recruiter" checked={hasRecruiter} onChange={setHasRecruiter} data-id="element-1278" data-mp="1deeo6546mnpd" />
        <ToggleRow label="Find a leader" checked={hasLeader} onChange={setHasLeader} data-id="element-1279" data-mp="1n5hlqwxwkkjw" />
        <ToggleRow label="Go to market" checked={goToMarket && hasLeader} onChange={next => {
        setGoToMarket(next);
        // A go-to-market proposal starts with the leader search.
        if (next) setHasLeader(true);
      }} data-id="element-1280" data-mp="215k4smhy5k0q" />
      </div>

      <div className="mt-3 pt-3 border-t border-slate-800" data-id="element-1281" data-mp="2qfml5554iuo">
        <div className={sectionLabel} data-id="element-1282" data-mp="21ufikaku4a2r">Pricing</div>
        <div className="mt-1.5" data-id="element-1283" data-mp="21vpiz6f5tuck">
          <ToggleRow label="Implementation fee" checked={pricingModel === 'flagship'} onChange={next => setPricingModel(next ? 'flagship' : 'monthly')} data-id="element-1284" data-mp="21wif7dght1gg" />
        </div>
        {pricingModel === 'flagship' && <div className="mt-1.5" data-id="element-1285" data-mp="2gsq4gs6rct2l">
            <div role="group" aria-label="How the implementation fee is paid" className="grid grid-cols-3 gap-1" data-id="element-1286" data-mp="2gplqj33hqiyq">
              {IMPLEMENTATION_OPTIONS.map(option => {
            // Split and promo combine; "In full" means neither.
            const active = option.key === 'full' ? !promoActive && !splitActive : option.key === 'split' ? splitActive : promoActive;
            return <button key={option.key} type="button" aria-pressed={active} onClick={() => setImplementationOption(option.key)} className={`rounded-md border px-1 py-1 text-[11.5px] font-semibold whitespace-nowrap transition-colors duration-150 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7FE0C3] ${active ? 'border-[#14684d] bg-[#14684d] text-white' : 'border-slate-700 text-slate-400 hover:text-white hover:border-slate-500'}`} data-id="element-1287" data-mp="38jw2n32x4gp8">
                    {option.key === 'promo' ? `${promoImplementationFee(hasRecruiter) / 1000}K promo` : option.label}
                  </button>;
          })}
            </div>
            <p className="mt-1.5 text-[11.5px] leading-snug text-slate-500" data-id="element-1288" data-mp="1zarhhide1w33">
              {implementationHint}
            </p>
            {promoActive && <input type="date" aria-label="Promo deadline" value={promoDeadline} onChange={e => e.target.value && setPromoDeadline(e.target.value)} className="mt-1 w-full rounded-md border border-slate-700 bg-[#0d141f] px-2 py-1.5 text-[13px] text-slate-200 [color-scheme:dark] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7FE0C3]" data-id="element-1289" data-mp="12v6he3usvoa4" />}
          </div>}
        <label className="mt-2 block" data-id="element-1290" data-mp="1qlaia3glt9xf">
          <span className="text-[12px] text-slate-400" data-id="element-1291" data-mp="1qk7va6mejrms">Page 2 layout</span>
          <select value={pricingLayout} onChange={e => setPricingLayout(e.target.value as PricingLayout)} className="mt-1 w-full rounded-md border border-slate-700 bg-[#0d141f] px-2 py-1.5 text-[13px] text-slate-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7FE0C3]" data-id="element-1292" data-mp="1cm1419bykpw3">
            <option value="row" data-id="element-1293" data-mp="670i7wgndo38">A · Four-figure row</option>
            <option value="tiers" data-id="element-1294" data-mp="3tt9u1iiha4bb">Tier table</option>
            <option value="twoStep" data-id="element-1295" data-mp="3trzv6r3sws3m">B · To start, then monthly</option>
            <option value="feeList" data-id="element-1296" data-mp="3pr681zy6nhbs">C · Fee list + hires</option>
            <option value="tiersStrip" data-id="element-1297" data-mp="7mclxiuugc7k">D · Tier table + fee strip</option>
          </select>
        </label>
      </div>

      <div className="mt-3 pt-3 border-t border-slate-800" data-id="element-1298" data-mp="2n8h14eyd0n1">
        <div className={sectionLabel} data-id="element-1299" data-mp="2lp0rdtaqpl9">Guarantees</div>
        <div className="mt-1.5" data-id="element-1300" data-mp="3b438lnqauylt">
          <ToggleRow label="Money-back" checked={moneyBackGuarantee} onChange={setMoneyBackGuarantee} data-id="element-1301" data-mp="3b18q41qujnv1" />
          <ToggleRow label="Performance" checked={guaranteeRevealed} onChange={setGuaranteeRevealed} data-id="element-1302" data-mp="3ktec8uuf3b2b" />
        </div>
      </div>

      <div className="mt-4 pt-4 border-t border-slate-800" data-id="element-1303" data-mp="2sck5lgw9zkjs">
        <div className={sectionLabel} data-id="element-1304" data-mp="2sazczwtiubok">Monthly ad spend</div>
        <div className="mt-2 flex items-center gap-1.5" data-id="element-1305" data-mp="2neim4uuxtj7s">
          <button type="button" onClick={() => stepAdSpend(-1)} disabled={adSpend <= AD_SPEND_STEP} aria-label="Decrease ad spend by $2,500" className="grid place-items-center h-8 w-8 shrink-0 rounded-md border border-slate-700 text-slate-400 hover:text-white hover:border-slate-500 disabled:opacity-40 disabled:hover:text-slate-400 transition-colors duration-150 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7FE0C3]" data-id="element-1306" data-mp="2nbwfqyjs99r5">
            <MinusIcon className="w-3.5 h-3.5" data-id="element-1307" data-mp="1d93pci52pn04" />
          </button>
          <ControlNumberField label="Monthly ad spend" value={adSpend} min={AD_SPEND_STEP} onCommit={setAdSpend} className="flex-1" data-id="element-1308" data-mp="1d7iraijjvlxi" />
          <button type="button" onClick={() => stepAdSpend(1)} aria-label="Increase ad spend by $2,500" className="grid place-items-center h-8 w-8 shrink-0 rounded-md border border-slate-700 text-slate-400 hover:text-white hover:border-slate-500 transition-colors duration-150 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7FE0C3]" data-id="element-1309" data-mp="138jkz6m9c2do">
            <PlusIcon className="w-3.5 h-3.5" data-id="element-1310" data-mp="3exzszgmt49sk" />
          </button>
        </div>
      </div>

      <div className="mt-4" data-id="element-1311" data-mp="3ew6mq2g3vrl3">
        <div className={sectionLabel} data-id="element-1312" data-mp="3jxm51mqwfjzs">Ad cost per hire</div>
        <div className="mt-2 flex items-center gap-1.5" data-id="element-1313" data-mp="3juzxvo7x76i3">
          <ControlNumberField label="Cost per hire, low" value={cpaLow} min={1} onCommit={next => {
          setCpaLow(next);
          if (next > cpaHigh) setCpaHigh(next);
        }} className="flex-1" data-id="element-1314" data-mp="2maddpb47vgpv" />
          <span className="text-slate-600 text-[12px]" aria-hidden="true" data-id="element-1315" data-mp="30yhyqlqqs4bt">–</span>
          <ControlNumberField label="Cost per hire, high" value={cpaHigh} min={1} onCommit={next => setCpaHigh(Math.max(cpaLow, next))} className="flex-1" data-id="element-1316" data-mp="23j47pbtnna31" />
        </div>
      </div>

      <p className="mt-3 text-[12px] text-slate-500" data-id="element-1317" data-mp="20fetld66rky9">
        Projects{' '}
        <span className="font-semibold text-slate-300" data-id="element-1318" data-mp="1weuvo7dsgdb2">
          {hiresLow}–{hiresHigh} hires
        </span>{' '}
        / month
      </p>

      <AssumptionControls data-id="element-1319" data-mp="2a8vneh6bp01x" />
    </motion.aside>;
}